/**
 * Pure utilities for turning backend validation rule specs into user-facing
 * descriptions.
 *
 * Used by:
 * - `RequirementHintComponent` to render the inline short hint + the info-icon
 *   tooltip bullets next to every metadata input.
 * - The batch CSV upload modal's "Requirements for this source type" summary.
 *
 * Zero Angular dependencies so this module stays trivially unit-testable.
 */

import {
  ICrossFieldRuleSpec,
  IMetadataFieldSchema,
  IValidatorRuleSpec,
} from '../models/MetadataSchema';

export interface IRequirementDescriptor {
  /** Compact inline text, e.g. `"Required, 0-50"`. Empty when no rules apply. */
  shortHint: string;
  /** Bullet items for the info-icon tooltip / popover. */
  fullDetails: string[];
}

const HUMAN_FORMAT_LABELS: Record<string, string> = {
  alphanumeric: 'letters and numbers only',
  integer: 'whole number',
  positive_integer: 'positive whole number',
  positive_float: 'positive number',
  url: 'valid URL',
  email: 'valid email address',
  zipcode: 'valid ZIP code',
};

const DATA_TYPE_HINTS: Record<string, string> = {
  NUMBER: 'Number',
  DATE: 'Date',
  GPS: 'GPS coordinates (e.g. 38.98 N 77.11 W)',
};

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function describeNumericRange(params: Record<string, unknown>): string {
  const min = asNumber(params['min']);
  const max = asNumber(params['max']);
  const minInclusive = params['min_inclusive'] !== false; // default true
  const maxInclusive = params['max_inclusive'] !== false;

  if (min === null && max === null) {
    return 'Numeric value';
  }
  if (min !== null && max !== null) {
    if (minInclusive && maxInclusive) {
      return `Between ${min} and ${max} (inclusive)`;
    }
    if (!minInclusive && !maxInclusive) {
      return `Greater than ${min} and less than ${max}`;
    }
    return `${minInclusive ? '≥' : '>'} ${min} and ${maxInclusive ? '≤' : '<'} ${max}`;
  }
  if (min !== null) {
    return minInclusive ? `At least ${min}` : `Greater than ${min}`;
  }
  // max only
  return maxInclusive ? `At most ${max}` : `Less than ${max}`;
}

function describeRangeShort(params: Record<string, unknown>): string | null {
  const min = asNumber(params['min']);
  const max = asNumber(params['max']);
  if (min !== null && max !== null) {
    return `${min}-${max}`;
  }
  if (min !== null) {
    return `≥ ${min}`;
  }
  if (max !== null) {
    return `≤ ${max}`;
  }
  return null;
}

function describeTypeFormat(params: Record<string, unknown>): string {
  const fmt = String(params['format'] ?? '').toLowerCase();
  return HUMAN_FORMAT_LABELS[fmt] ?? `Format: ${fmt || 'see admin'}`;
}

function describeRegex(params: Record<string, unknown>): string {
  const friendly = params['friendly'];
  if (typeof friendly === 'string' && friendly.trim().length > 0) {
    return friendly;
  }
  const pattern = params['pattern'];
  if (typeof pattern === 'string' && pattern.length > 0) {
    return `Must match pattern ${pattern}`;
  }
  return 'Must match a specific pattern';
}

function describeUnique(params: Record<string, unknown>): string {
  const scope = String(params['scope'] ?? '').toUpperCase();
  if (scope === 'PLATFORM') {
    return 'Must be unique across the platform';
  }
  if (scope === 'LAB') {
    return 'Must be unique within the lab';
  }
  return 'Must be unique';
}

function describeOneRule(rule: IValidatorRuleSpec): string {
  switch (rule.rule_type) {
    case 'REQUIRED':
      return 'Required';
    case 'TYPE_FORMAT':
      return describeTypeFormat(rule.params);
    case 'NUMERIC_RANGE':
      return describeNumericRange(rule.params);
    case 'NUMERIC_SINGLE_OR_RANGE': {
      const requirePositive = rule.params['require_positive'] === true;
      return requirePositive
        ? 'Positive number, or a range like "10-20"'
        : 'Number, or a range like "10-20"';
    }
    case 'REGEX':
      return describeRegex(rule.params);
    case 'ENUM_MATCH':
      return 'Must be one of the listed options';
    case 'UNIQUE':
      return describeUnique(rule.params);
    case 'REFERENCES_MODEL':
      return 'Must reference an existing record';
    case 'MUTUALLY_EXCLUSIVE_VALUE': {
      const exclusive = String(rule.params['exclusive_value'] ?? '').trim();
      return exclusive
        ? `If "${exclusive}" is chosen, no other value can be selected`
        : 'Some values are mutually exclusive';
    }
    case 'DATE_NOT_FUTURE':
      return 'Cannot be in the future';
    case 'DATE_MAX_AGE': {
      const years = asNumber(rule.params['max_years']);
      return years !== null
        ? `Should be within the last ${years} year${years === 1 ? '' : 's'}`
        : 'Should not be too far in the past';
    }
    default:
      return `Rule: ${rule.rule_type}`;
  }
}

function describeOptions(field: IMetadataFieldSchema, max = 6): string | null {
  if (!field.options || field.options.length === 0) {
    return null;
  }
  const visible = field.options
    .map((o) => o.value ?? o.name ?? '')
    .filter((v) => v.length > 0);
  if (visible.length === 0) {
    return null;
  }
  if (visible.length <= max) {
    return `One of: ${visible.join(', ')}`;
  }
  return `One of ${visible.length} options: ${visible.slice(0, max).join(', ')}, ...`;
}

function describeDataType(field: IMetadataFieldSchema): string | null {
  if (!field.data_type) {
    return null;
  }
  return DATA_TYPE_HINTS[field.data_type] ?? null;
}

/**
 * Group rules by `rule_group`. Rules with an empty group are standalone;
 * rules sharing a non-empty group form an OR-set (any one passing satisfies
 * the field) and are described as such.
 */
function groupRules(rules: IValidatorRuleSpec[]): IValidatorRuleSpec[][] {
  const groups = new Map<string, IValidatorRuleSpec[]>();
  const standalone: IValidatorRuleSpec[][] = [];
  for (const rule of rules) {
    const key = rule.rule_group?.trim();
    if (!key) {
      standalone.push([rule]);
      continue;
    }
    const existing = groups.get(key);
    if (existing) {
      existing.push(rule);
    } else {
      groups.set(key, [rule]);
    }
  }
  return [...standalone, ...groups.values()];
}

function describeRuleGroup(group: IValidatorRuleSpec[]): string {
  if (group.length === 1) {
    return describeOneRule(group[0]);
  }
  const parts = group.map((rule) => describeOneRule(rule));
  return `Either: ${parts.join(' OR ')}`;
}

/**
 * Render a cross-field rule for display on a particular field's hint.
 *
 * The backend's `cross_field_validators.py` consistently treats the left
 * template as the field carrying the constraint, and the right template as the
 * reference / condition / source field. For the asymmetric rule types we
 * always render next to the left template and reference the right template
 * as "the other field" — e.g. for "Date Sequenced (left) >= Date Collected
 * (right)", produce "Must be on or after Date Collected" to be shown next to
 * Date Sequenced.
 *
 * `MUTUALLY_EXCLUSIVE_VALUE` is symmetric, so we resolve "the other field"
 * based on `currentTemplateId` to avoid the hint pointing back at itself.
 */
export function describeCrossFieldRuleForLeftTemplate(
  rule: ICrossFieldRuleSpec,
  currentTemplateId?: number,
): string {
  const otherField =
    currentTemplateId !== undefined && rule.left_template_id === currentTemplateId
      ? rule.right_template_name || 'another field'
      : currentTemplateId !== undefined && rule.right_template_id === currentTemplateId
        ? rule.left_template_name || 'another field'
        : rule.right_template_name || 'another field';

  switch (rule.rule_type) {
    case 'CROSS_FIELD_COMPARE': {
      const op = String(rule.params['op'] ?? '').toLowerCase();
      const map: Record<string, string> = {
        eq: 'equal to',
        ne: 'not equal to',
        gt: 'after',
        gte: 'on or after',
        lt: 'before',
        lte: 'on or before',
      };
      const phrase = map[op] ?? `compared (${op}) to`;
      return `Must be ${phrase} ${otherField}`;
    }
    case 'CONDITIONAL_REQUIRED': {
      const conditionOp = String(rule.params['condition_op'] ?? '').toLowerCase();
      const conditionValueRaw = rule.params['condition_value'];
      const values = Array.isArray(conditionValueRaw)
        ? conditionValueRaw.map((v) => String(v)).join(', ')
        : String(conditionValueRaw ?? '');
      if (conditionOp === 'not_in') {
        return `Required when ${otherField} is set to anything other than: ${values}`;
      }
      if (conditionOp === 'in') {
        return `Required when ${otherField} is: ${values}`;
      }
      return `Required based on ${otherField}`;
    }
    case 'CROSS_FIELD_VALUE_DEPENDENCY': {
      const source = String(rule.params['source_value'] ?? '');
      const allowed = rule.params['allowed_target_values'];
      const allowedSummary = Array.isArray(allowed) && allowed.length > 0
        ? `${allowed.length} value${allowed.length === 1 ? '' : 's'} allowed`
        : 'restricted values';
      return source
        ? `When ${otherField} is "${source}": ${allowedSummary}`
        : `Depends on ${otherField}`;
    }
    case 'MUTUALLY_EXCLUSIVE_VALUE': {
      const exclusive = String(rule.params['exclusive_value'] ?? '');
      return exclusive
        ? `Cannot be set together with "${exclusive}" on ${otherField}`
        : `Mutually exclusive with ${otherField}`;
    }
    default:
      return `Linked to ${otherField}`;
  }
}

/**
 * Pull cross-field rules out of the schema where the given template id is the
 * *left* (constrained) side. These rules should be displayed on that field's
 * hint, because that's the field the user must adjust to satisfy the rule.
 *
 * `MUTUALLY_EXCLUSIVE_VALUE` is symmetric — the constraint applies in both
 * directions — so it is matched if the template id appears on either side.
 */
export function crossFieldRulesForLeftTemplate(
  allCrossRules: ICrossFieldRuleSpec[],
  templateId: number,
): ICrossFieldRuleSpec[] {
  return allCrossRules.filter((r) => {
    if (r.rule_type === 'MUTUALLY_EXCLUSIVE_VALUE') {
      return r.left_template_id === templateId || r.right_template_id === templateId;
    }
    return r.left_template_id === templateId;
  });
}

/**
 * Top-level entry point. Combines:
 * - The template's implicit "Required" flag (if no explicit REQUIRED rule).
 * - Data-type hints (e.g. GPS format example).
 * - Active per-field rules.
 * - Allowed options for SELECT-style fields.
 * - Cross-field rules attached to this template as the right-hand side.
 */
export function describeField(
  field: IMetadataFieldSchema,
  crossFieldRules: ICrossFieldRuleSpec[] = [],
): IRequirementDescriptor {
  const fullDetails: string[] = [];
  const shortParts: string[] = [];

  const hasExplicitRequired = field.rules.some((r) => r.rule_type === 'REQUIRED');
  if (field.is_required || hasExplicitRequired) {
    fullDetails.push('Required');
    shortParts.push('Required');
  }

  const dataTypeHint = describeDataType(field);
  if (dataTypeHint) {
    fullDetails.push(dataTypeHint);
  }

  for (const group of groupRules(field.rules)) {
    if (group.length === 1 && group[0].rule_type === 'REQUIRED') {
      continue;
    }
    const text = describeRuleGroup(group);
    fullDetails.push(text);

    if (shortParts.length < 2) {
      if (group.length === 1) {
        const rule = group[0];
        if (rule.rule_type === 'NUMERIC_RANGE') {
          const short = describeRangeShort(rule.params);
          if (short) {
            shortParts.push(short);
            continue;
          }
        }
        if (rule.rule_type === 'TYPE_FORMAT') {
          shortParts.push(describeTypeFormat(rule.params));
          continue;
        }
        if (rule.rule_type === 'DATE_NOT_FUTURE') {
          shortParts.push('not future');
          continue;
        }
      }
    }
  }

  const optionsText = describeOptions(field);
  if (optionsText) {
    fullDetails.push(optionsText);
  }

  for (const crossRule of crossFieldRulesForLeftTemplate(crossFieldRules, field.id)) {
    fullDetails.push(describeCrossFieldRuleForLeftTemplate(crossRule, field.id));
  }

  return {
    shortHint: shortParts.join(', '),
    fullDetails,
  };
}
